import { useState, useEffect, useCallback } from 'react';
import { CheckCircle, RotateCcw } from 'lucide-react';
import { rpc } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { EmptyState } from '@/components/common/EmptyState';
import { Pagination } from '@/components/common/Pagination';
import { FilterBar } from '@shared/search';
import { bugsSchema } from './bugsSchema';
import { BugDetailDialog, type BugReport } from '@/components/bugs/BugDetailDialog';
import { useToast } from '@/context/ToastContext';
import { useI18n } from '@/context/I18nContext';

interface ListResponse {
  bugs: BugReport[];
  totalCount: number;
  page: number;
}

function getHashParams(): URLSearchParams {
  const hash = window.location.hash;
  const qIdx = hash.indexOf('?');
  return new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx) : '');
}

function updateHashParams(page: number, query: string) {
  const hash = window.location.hash;
  const base = hash.includes('?') ? hash.slice(0, hash.indexOf('?')) : hash;
  const sp = new URLSearchParams();
  if (page > 1) sp.set('page', String(page));
  if (query) sp.set('q', query);
  const qs = sp.toString();
  window.history.replaceState(null, '', qs ? `${base}?${qs}` : base);
}

const PAGE_SIZE = 20;

export function BugsTab() {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [bugs, setBugs] = useState<BugReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(() => {
    const p = parseInt(getHashParams().get('page') || '1');
    return p > 0 ? p : 1;
  });
  const [query, setQuery] = useState(() => getHashParams().get('q') ?? 'status:open');
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedBug, setSelectedBug] = useState<BugReport | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const loadBugs = useCallback(async (p: number, q: string) => {
    setLoading(true);
    try {
      const body: Record<string, unknown> = { page: p, pageSize: PAGE_SIZE };
      if (q) body.query = q;
      const data = await rpc<ListResponse>('ListBugReports', body);
      setBugs(data.bugs ?? []);
      setTotalCount(data.totalCount ?? 0);
      setTotalPages(Math.max(1, Math.ceil((data.totalCount ?? 0) / PAGE_SIZE)));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadBugs(page, query);
  }, [page, query, loadBugs]);

  useEffect(() => {
    updateHashParams(page, query);
  }, [page, query]);

  useEffect(() => {
    const handleHashSync = () => {
      const p = parseInt(getHashParams().get('page') || '1');
      const q = getHashParams().get('q') ?? 'status:open';
      setPage(p > 0 ? p : 1);
      setQuery(q);
    };
    window.addEventListener('hashchange', handleHashSync);
    window.addEventListener('popstate', handleHashSync);
    return () => {
      window.removeEventListener('hashchange', handleHashSync);
      window.removeEventListener('popstate', handleHashSync);
    };
  }, []);

  const handleSearch = (newQuery: string) => {
    setPage(1);
    setQuery(newQuery);
  };

  const handleAction = async (id: string, action: 'resolve' | 'reopen', e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setActionLoading(true);
    try {
      if (action === 'resolve') {
        await rpc('ResolveBugReport', { id });
        showToast(t('bugs.resolved'));
      } else {
        await rpc('ReopenBugReport', { id });
        showToast(t('bugs.reopened'));
      }
      setSelectedBug(null);
      loadBugs(page, query);
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const countLabel = totalCount === 1 
    ? t('bugs.countSingular') 
    : t('bugs.count', { count: String(totalCount) });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h2 className="text-[1.4rem] font-bold text-text-primary tracking-tight">{t('bugs.title')}</h2>
          {!loading && totalCount > 0 && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-bg-surface border border-border-default text-text-muted">
              {countLabel}
            </span>
          )}
        </div>
      </div>

      {/* Filter Toolbar */}
      <div className="mb-6">
        <FilterBar
          schema={bugsSchema}
          initialQuery={query}
          onSearch={handleSearch}
          enableFilterUI={true}
        />
      </div>

      {loading ? (
        <div className="card overflow-hidden">
          <LoadingSpinner message={t('bugs.loading')} />
        </div>
      ) : error ? (
        <ErrorState message={error} />
      ) : bugs.length === 0 ? (
        <div className="card">
          <EmptyState icon="🐛" message={t('bugs.empty')} />
        </div>
      ) : (
        <div className="animate-fade-in">
          <div className="card overflow-hidden">
            {/* Desktop table */}
            <table className="w-full border-collapse hidden md:table">
              <thead>
                <tr className="divider">
                  <th className="w-[100px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('bugs.status')}
                  </th>
                  <th className="w-[180px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('bugs.reporter')}
                  </th>
                  <th className="px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('bugs.description')}
                  </th>
                  <th className="w-[140px] px-4 py-2.5 text-left text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted">
                    {t('bugs.createdAt')}
                  </th>
                  <th className="w-[60px] text-right px-4 py-2.5 text-[0.7rem] font-semibold uppercase tracking-wider text-text-muted" />
                </tr>
              </thead>
              <tbody>
                {bugs.map((bug, i) => {
                  const isOpen = bug.status === 'open';

                  return (
                    <tr
                      key={bug.id}
                      onClick={() => setSelectedBug(bug)}
                      className={`hover:bg-bg-hover transition-colors cursor-pointer ${i < bugs.length - 1 ? 'divider' : ''}`}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {isOpen ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-green/10 text-green border border-green/20 uppercase tracking-wider">
                              {t('bugs.statusOpen')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-bg-surface text-text-muted border border-border-default uppercase tracking-wider">
                              {t('bugs.statusResolved')}
                            </span>
                          )}
                          {bug.environment && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.65rem] font-medium bg-bg-surface text-text-muted border border-border-default uppercase mono">
                              {bug.environment}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium text-text-primary truncate block">{bug.reporterName}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-text-secondary truncate block">{bug.description}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs text-text-secondary whitespace-nowrap mono">
                          {new Date(bug.createdAt).toLocaleDateString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isOpen ? (
                          <button
                            onClick={(e) => handleAction(bug.id, 'resolve', e)}
                            disabled={actionLoading}
                            className="inline-flex items-center justify-center w-7 h-7 rounded text-accent hover:bg-accent/10 transition-colors disabled:opacity-50 cursor-pointer"
                            title={t('bugs.resolve')}
                          >
                            <CheckCircle size={16} />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => handleAction(bug.id, 'reopen', e)}
                            disabled={actionLoading}
                            className="inline-flex items-center justify-center w-7 h-7 rounded text-yellow hover:bg-yellow/10 transition-colors disabled:opacity-50 cursor-pointer"
                            title={t('bugs.reopen')}
                          >
                            <RotateCcw size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* Mobile Layout */}
            <div className="md:hidden">
              {bugs.map((bug, i) => {
                const isOpen = bug.status === 'open';
                return (
                  <div
                    key={bug.id}
                    onClick={() => setSelectedBug(bug)}
                    className={`p-4 hover:bg-bg-hover transition-colors cursor-pointer ${i < bugs.length - 1 ? 'divider' : ''}`}
                  >
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {isOpen ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-green/10 text-green border border-green/20 uppercase tracking-wider">
                              {t('bugs.statusOpen')}
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.65rem] font-bold bg-bg-surface text-text-muted border border-border-default uppercase tracking-wider">
                              {t('bugs.statusResolved')}
                            </span>
                          )}
                          {bug.environment && (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[0.65rem] font-medium bg-bg-surface text-text-muted border border-border-default uppercase mono">
                              {bug.environment}
                            </span>
                          )}
                          <div className="text-sm font-medium text-text-primary truncate">{bug.reporterName}</div>
                        </div>
                        <div className="text-sm text-text-secondary line-clamp-2 mt-1">{bug.description}</div>
                        <div className="text-xs text-text-muted mt-2 mono">
                          {new Date(bug.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="shrink-0">
                        {isOpen ? (
                          <button
                            onClick={(e) => handleAction(bug.id, 'resolve', e)}
                            disabled={actionLoading}
                            className="inline-flex items-center justify-center w-8 h-8 rounded text-accent hover:bg-accent/10 transition-colors disabled:opacity-50 cursor-pointer"
                            title={t('bugs.resolve')}
                          >
                            <CheckCircle size={18} />
                          </button>
                        ) : (
                          <button
                            onClick={(e) => handleAction(bug.id, 'reopen', e)}
                            disabled={actionLoading}
                            className="inline-flex items-center justify-center w-8 h-8 rounded text-yellow hover:bg-yellow/10 transition-colors disabled:opacity-50 cursor-pointer"
                            title={t('bugs.reopen')}
                          >
                            <RotateCcw size={18} />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      <BugDetailDialog 
        bug={selectedBug} 
        onClose={() => setSelectedBug(null)} 
        onAction={(id, action) => handleAction(id, action)}
        loading={actionLoading}
      />
    </div>
  );
}
